import { Component } from '@theme/component';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that manages product upsells with checkboxes.
 * Handles adding upsell products to the cart when the main product is added.
 *
 * @extends Component
 */
class ProductUpsellComponent extends Component {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    // Find the product form
    this.productForm = this.closest('product-form-component');

    if (!this.productForm) {
      console.warn('Product upsell: Could not find product-form-component');
      return;
    }

    // Listen for successful cart add events to add upsell products
    const { signal } = this.#abortController;
    document.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate.bind(this), { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Handles cart update events to check if we need to add upsell products
   * @param {CustomEvent} event - The cart update event
   */
  async handleCartUpdate(event) {
    // Only handle events from the product form component
    if (event.detail?.data?.source !== 'product-form-component') {
      return;
    }

    // Don't process if there was an error
    if (event.detail?.data?.didError) {
      return;
    }

    // Find all checked upsell checkboxes within this component
    const checkedUpsells = this.querySelectorAll('.product-upsell__checkbox:checked');

    if (checkedUpsells.length === 0) {
      // No upsells selected
      return;
    }

    // Add upsell products to cart
    const upsellItems = [];
    checkedUpsells.forEach((checkbox) => {
      if (checkbox instanceof HTMLInputElement) {
        const variantId = checkbox.dataset.upsellVariantId;
        if (variantId) {
          upsellItems.push({
            id: variantId,
            quantity: 1
          });
        }
      }
    });

    if (upsellItems.length > 0) {
      await this.addUpsellsToCart(upsellItems);
    }
  }

  /**
   * Adds upsell items to the cart
   * @param {Array<{id: string, quantity: number}>} items - Array of items to add {id, quantity}
   */
  async addUpsellsToCart(items) {
    try {
      // Get cart section IDs for update
      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      const sections = Array.from(cartItemsComponents)
        .filter(item => {
          if (item instanceof HTMLElement) {
            return item.dataset?.sectionId;
          }
          return false;
        })
        .map(item => {
          if (item instanceof HTMLElement) {
            return item.dataset.sectionId;
          }
          return '';
        })
        .filter(id => id)
        .join(',');

      // Prepare request body
      const body = {
        items: items,
        sections: sections
      };

      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(body)
      });

      const data = await response.json();

      if (data.status) {
        // Error occurred
        this.handleError(data);
      } else {
        // Success
        this.handleSuccess(data, items);
      }
    } catch (error) {
      console.error('Error adding upsell items to cart:', error);
      this.handleError({ message: 'An error occurred while adding items to cart' });
    }
  }

  /**
   * Handles successful cart add
   * @param {any} data - Response data
   * @param {Array<{id: string, quantity: number}>} items - Items that were added
   */
  handleSuccess(data, items) {
    // Calculate total quantity added
    const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);

    // Dispatch cart update event
    const event = new CartUpdateEvent(data, 'product-upsell', {
      source: 'product-upsell',
      itemCount: totalQuantity,
      sections: data.sections || {}
    });

    document.dispatchEvent(event);

    // Uncheck upsell checkboxes after successful add
    const checkedUpsells = this.querySelectorAll('.product-upsell__checkbox:checked');
    checkedUpsells.forEach(checkbox => {
      if (checkbox instanceof HTMLInputElement) {
        checkbox.checked = false;
      }
    });
  }

  /**
   * Handles errors during cart add
   * @param {any} data - Error data
   */
  handleError(data) {
    console.error('Product upsell error:', data);

    // Show error in live region if available
    const liveRegion = this.productForm?.querySelector('[ref="liveRegion"]');
    if (liveRegion) {
      liveRegion.textContent = data.message || 'Error adding upsell to cart';
      setTimeout(() => {
        liveRegion.textContent = '';
      }, 10000);
    }
  }
}

if (!customElements.get('product-upsell-component')) {
  customElements.define('product-upsell-component', ProductUpsellComponent);
}
